import React from 'react';
import { Link } from 'react-router-dom';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import SideBarContainer from 'Containers/SideBarContainer';
import BarStat from './components/molecules/BarStat/BarStat';
import NextStepPanel from './components/molecules/NextStepPanel/NextStepPanel';
import PendingList from './components/organisms/PendingList/PendingList';
import ApprovedCoursesGrid from './components/organisms/ApprovedCoursesGrid/ApprovedCoursesGrid';
import NewsRail from './components/organisms/NewsRail/NewsRail';
import DashboardSkeleton from './components/organisms/DashboardSkeleton/DashboardSkeleton';
import HelpDrawer from './components/organisms/HelpDrawer/HelpDrawer';
import NewsDetailModal from './components/organisms/NewsDetailModal/NewsDetailModal';
import useDashboardPage, { MY_COURSES_PATH } from './hooks/useDashboardPage';
import {
  Page,
  HeaderRow,
  WelcomeSub,
  HelpButton,
  HeroRow,
  Layout,
  MainCol,
  SectionBlock,
  RightRail,
  Edcap,
  ErrorBox,
} from './Dashboard.styled';

export default function Dashboard() {
  const {
    t2,
    userName,
    loading,
    error,
    retry,
    assignedCount,
    pendingCount,
    approvedCount,
    failedCount,
    nextCourse,
    pendingCourses,
    approvedRows,
    newsHero,
    newsItems,
    helpOpen,
    openHelp,
    closeHelp,
    selectedNews,
    openNews,
    closeNews,
    onResumeCourse,
    onViewCourse,
  } = useDashboardPage();

  if (loading) {
    return (
      <SideBarContainer navBar={false}>
        <DashboardSkeleton />
      </SideBarContainer>
    );
  }

  if (error) {
    return (
      <SideBarContainer navBar={false}>
        <ErrorBox>
          {t2('LoadError')}
          <button type="button" onClick={retry}>
            {t2('Retry')}
          </button>
        </ErrorBox>
      </SideBarContainer>
    );
  }

  return (
    <SideBarContainer navBar={false}>
      <Page>
        <HelpDrawer
          open={helpOpen}
          onClose={closeHelp}
          title={t2('Help') as string}
          intro={t2('HelpIntro') as string}
          sectionsTitle={t2('HelpSectionsTitle') as string}
          sections={[
            { name: t2('HelpDashboardName') as string, text: t2('HelpDashboardText') as string },
            { name: t2('HelpMyCoursesName') as string, text: t2('HelpMyCoursesText') as string },
            { name: t2('HelpCourseName') as string, text: t2('HelpCourseText') as string },
          ]}
          closeLabel={t2('HelpClose') as string}
        />

        <NewsDetailModal news={selectedNews} onClose={closeNews} closeLabel={t2('NewsClose') as string} />

        <Layout>
          <MainCol>
            <HeaderRow>
              <WelcomeSub>
                {t2('WelcomeBack')} <b>{userName}</b>
              </WelcomeSub>
              <HelpButton type="button" aria-label={t2('Help') as string} onClick={openHelp}>
                <HelpOutlineIcon fontSize="small" />
                <span>{t2('Help')}</span>
              </HelpButton>
            </HeaderRow>

            <HeroRow>
              <BarStat
                pendingCount={pendingCount}
                approvedCount={approvedCount}
                failedCount={failedCount}
                totalCount={assignedCount}
                label={t2('PendingRingLabel') as string}
                approvedLabel={t2('LegendApproved') as string}
                pendingLabel={t2('LegendPending') as string}
                failedLabel={t2('LegendFailed') as string}
              />
              <NextStepPanel
                eyebrow={t2('NextStep') as string}
                title={nextCourse ? nextCourse.name : t2('EmptyPending')}
                meta={
                  nextCourse && (
                    <>
                      {t2('Modules', [String(nextCourse.moduleCount)])}
                      {nextCourse.dueDate && <> · {t2('Due', [nextCourse.dueDate])}</>}
                    </>
                  )
                }
                ctaLabel={nextCourse ? (t2('ResumeCourse') as string) : null}
                onCta={onResumeCourse}
              />
            </HeroRow>

            <SectionBlock>
              <Edcap>{t2('PendingRoute')}</Edcap>
              <PendingList items={pendingCourses} emptyText={t2('EmptyPending') as string} onItemClick={onViewCourse} />
              <Edcap style={{ marginTop: 28 }}>
                {t2('ApprovedCourses')} — {approvedCount} · <Link to={MY_COURSES_PATH}>{t2('MyCourses')}</Link>
              </Edcap>
              <ApprovedCoursesGrid
                rows={approvedRows}
                emptyText={t2('EmptyApproved') as string}
                onTileClick={onViewCourse}
              />
            </SectionBlock>
          </MainCol>
          {(newsHero !== null || newsItems.length > 0) && (
            <RightRail>
              <Edcap>{t2('News')}</Edcap>
              <NewsRail
                emptyText={t2('EmptyNews') as string}
                hero={newsHero}
                items={newsItems}
                onItemClick={openNews}
              />
            </RightRail>
          )}
        </Layout>
      </Page>
    </SideBarContainer>
  );
}
